import Papa from 'papaparse'
import * as XLSX from 'xlsx'

// Generic CSV/TSV/XLS/XLSX helpers for the import wizard. Deliberately knows
// nothing about any CRM: destination fields arrive as a `template` argument, so
// the same parsing and mapping serve whatever schema the caller supplies.

export const DELIMITED_EXTENSIONS = ['.csv', '.tsv']
export const SPREADSHEET_EXTENSIONS = ['.xls', '.xlsx']
export const ACCEPTED_EXTENSIONS = [...DELIMITED_EXTENSIONS, ...SPREADSHEET_EXTENSIONS]
export const ACCEPTED_LABEL = 'CSV, TSV, XLS, or XLSX'

const norm = (value) => String(value ?? '').trim().toLowerCase()
const isFilled = (value) => String(value ?? '').trim() !== ''

export const parseCsvText = (text) => {
    const results = Papa.parse(String(text ?? ''), {
        header: false,
        skipEmptyLines: 'greedy',
    })
    return {
        rows: results.data || [],
        errors: results.errors || [],
    }
}

export const readFileText = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(String(reader.result ?? ''))
        reader.onerror = () => reject(new Error('Could not read that file.'))
        reader.readAsText(file, 'utf-8')
    })

export const readFileArrayBuffer = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result)
        reader.onerror = () => reject(new Error('Could not read that file.'))
        reader.readAsArrayBuffer(file)
    })

/** 'delimited' | 'spreadsheet' | null — null means we do not accept the file. */
export const getFileKind = (name) => {
    const lower = String(name ?? '').toLowerCase()
    if (DELIMITED_EXTENSIONS.some((ext) => lower.endsWith(ext))) return 'delimited'
    if (SPREADSHEET_EXTENSIONS.some((ext) => lower.endsWith(ext))) return 'spreadsheet'
    return null
}

export const hasAcceptedExtension = (name) => getFileKind(name) !== null

/**
 * First worksheet only, matching the old importer, which read
 * workbook.Sheets[SheetNames[0]] and told the user to upload other sheets
 * separately. sheetList comes back so the caller can repeat that notice.
 *
 * SheetJS yields ragged, sparse rows whose cells may be numbers or undefined;
 * they are densified to strings here so everything downstream sees exactly the
 * shape PapaParse produces.
 */
export const parseWorkbook = (buffer) => {
    const workbook = XLSX.read(new Uint8Array(buffer), { type: 'array' })
    const sheetList = workbook?.SheetNames || []

    if (!sheetList.length) return { rows: [], errors: [], sheetList }

    const grid = XLSX.utils.sheet_to_json(workbook.Sheets[sheetList[0]], {
        header: 1,
        defval: '',
        blankrows: false,
    })

    const rows = grid
        .map((row) => Array.from(row || [], (cell) => String(cell ?? '')))
        .filter((row) => row.some(isFilled))

    return { rows, errors: [], sheetList }
}

/** One entry point for every accepted format. */
export const parseFile = async (file) => {
    if (getFileKind(file?.name) === 'spreadsheet') {
        return parseWorkbook(await readFileArrayBuffer(file))
    }
    return { ...parseCsvText(await readFileText(file)), sheetList: [] }
}

/**
 * The header is the first of the leading rows carrying the most filled cells,
 * which skips the preamble lines legacy CRM exports put above the real header.
 */
export const detectHeaderRow = (rows) => {
    if (!rows?.length) return 0
    const filled = rows.slice(0, 10).map((row) => (row || []).filter(isFilled).length)
    const widest = Math.max(...filled)
    if (widest === 0) return 0
    return filled.indexOf(widest)
}

export const getHeaders = (rows, headerRowIndex) =>
    (rows?.[headerRowIndex] || []).map((header, index) =>
        isFilled(header) ? String(header).trim() : `Column ${index + 1}`
    )

export const getDataRows = (rows, headerRowIndex) => (rows || []).slice(headerRowIndex + 1)

/** Source column index → template key, guessed from name and suggested_mappings. */
export const autoMapColumns = (headers, template) => {
    const mapping = {}
    ;(headers || []).forEach((header, index) => {
        const label = norm(header)
        if (!label) return
        const match = (template || []).find(
            (field) =>
                norm(field.name) === label ||
                (field.suggested_mappings || []).some((suggestion) => norm(suggestion) === label)
        )
        if (match) mapping[index] = match.key
    })
    return mapping
}

/** The library's defaultCombiner: non-empty values, space joined. */
export const combineValues = (values) =>
    (values || [])
        .filter((value) => value != null)
        .map((value) => String(value))
        .filter((value) => value.trim())
        .join(' ')

/** One row's mapped values. Several source columns on one key are combined. */
export const mapRowValues = (row, mapping) => {
    const collected = {}
    ;(row || []).forEach((value, sourceIndex) => {
        const key = mapping?.[sourceIndex]
        if (!key) return
        if (!collected[key]) collected[key] = []
        collected[key].push(value)
    })

    const values = {}
    Object.keys(collected).forEach((key) => {
        values[key] = combineValues(collected[key])
    })
    return values
}

/** Required template fields no source column is mapped onto. */
export const getMissingRequiredMappings = (template, mapping) => {
    const mapped = new Set(Object.values(mapping || {}).filter(Boolean))
    return (template || []).filter((field) => field.required && !mapped.has(field.key))
}

// ── Canonical rows for the import API ───────────────────────────────────────
//
// Everything above is generic parsing. What follows turns a mapped sheet into
// exactly the payload `crm/imports/serializers.py` accepts, so the request is
// built in one place and preview and commit can never disagree about it.

/** Deal ids as an array of numbers, from "17225;17228", a single value, or blank. */
export const normalizeDealIds = (value) => {
    if (value == null || value === '') return []
    const parts = Array.isArray(value) ? value : String(value).split(';')
    const ids = []
    parts.forEach((part) => {
        const trimmed = String(part ?? '').trim()
        if (!trimmed) return
        const parsed = Number(trimmed)
        // A non-numeric cell is dropped rather than throwing: the backend
        // reports it per row, and one odd cell must not break the upload.
        if (Number.isFinite(parsed) && !ids.includes(parsed)) ids.push(parsed)
    })
    return ids
}

/** Fields the API takes as a list rather than a string. */
const LIST_KEYS = ['legacy_deal_ids']

export const buildCanonicalImportRows = ({rows, headerRowIndex, mapping, template}) =>
    getDataRows(rows, headerRowIndex).map((row, index) => {
        const values = mapRowValues(row, mapping)
        const canonical = {row_index: index}

        ;(template || []).forEach((field) => {
            const raw = values[field.key]
            if (LIST_KEYS.includes(field.key)) {
                canonical[field.key] = normalizeDealIds(raw)
                return
            }
            // Only send fields the user mapped AND that carry a value. A blank
            // cell must be omitted, not sent as '': the API's date and decimal
            // fields reject an empty string, so an unfilled optional column
            // would fail the whole row. Omitted keys take the serializer's
            // default (null date, 0.00 profit, UNPAID status). Required fields
            // stay required — omitting a blank name still reports it.
            if (raw !== undefined && raw !== '') canonical[field.key] = raw
        })

        return canonical
    })
